// @flow

import { LOCATION_CHANGE } from "connected-react-router";
import { startRequest, finishRequest } from "@store/utils";

import {
  ExportAlertDetailsReport,
  ExportAlertDetailsReportSuccess,
  GetReportingAlertDetailsDefaultReports,
  GetReportingAlertDetailsDefaultReportsSuccess,
  GetReportingAlertDetailsDefaultReportsError,
  GetReportingAlertDetailsMyReports,
  GetReportingAlertDetailsMyReportsSuccess,
  GetReportingAlertDetailsMyReportsError,
  GetReportingAlertDetailsReport,
  GetReportingAlertDetailsReportSuccess,
  GetReportingAlertDetailsReportError,
  GetReportingAlertDetailsChartsSuccess,
  GetReportingAlertDetailsCharts,
  GetReportingAlertDetailsChartsError,
  GetAlertDetailsFiltersSchemaSuccess,
  GetAlertDetailsFiltersSchemaError,
  SetAlertDetailsReportToSave,
  SetAlertDetailsReportViewState,
  ExportAlertDetailsReportError,
  GetReportingAlertDetailsDefaultReportSuccess,
} from "@modules/reporting/constants/actions";
import type { AlertDetailsState, AlertDetailsReportViewState } from "./types";
import { cloneObject } from "@utils/object";

export const initialViewState: AlertDetailsReportViewState = {
  filtersExpanded: false,
  chartTop5PositionsExpanded: true,
  chartTop5AlertsExpanded: true,
  reportTableExpanded: true,
};

export const initialState: AlertDetailsState = {
  error: null,
  requestsInProgress: 0,
  reportGenerateInProgress: 0,
  defaultReports: [],
  mySavedReports: [],
  currentReport: null,
  currentCharts: null,
  reportData: {
    content: [],
    pagination: {
      sort: { orders: [] },
      pageNumber: 0,
      pageSize: 0,
      offset: 0,
    },
    totalElements: 0,
  },
  alertDetailsFiltersSchema: null,
  reportToSave: null,
  viewState: initialViewState,
};

export const setLoading = (state: AlertDetailsState) => () => startRequest(state);

export const onError = (state: AlertDetailsState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};
export const onGetReportingAlertDetailsReport = (state: AlertDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...startRequest(state),
    error: null,
    currentReport: payload,
  };
};

export const onGetReportingAlertDetailsDefaultReportSuccess = (state: AlertDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    currentReport: {
      report: payload,
    },
  };
};

export const onGetReportingAlertDetailsChartsSuccess = (state: AlertDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    currentCharts: payload,
  };
};

export const onGetReportingAlertDetailsDefaultReportsSuccess = (state: AlertDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    defaultReports: payload,
  };
};

export const onGetReportingAlertDetailsReportSuccess = (state: AlertDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    reportData: payload,
  };
};

export const onGetReportingAlertDetailsMySavedReportsSuccess = (state: AlertDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    mySavedReports: payload,
  };
};

export const onGetAlertDetailsFiltersSchemaSuccess = (state: AlertDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    alertDetailsFiltersSchema: payload,
  };
};

export const onSetAlertDetailsReportToSave = (state: AlertDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    reportToSave: payload,
  };
};

export const onSetViewState = (state: AlertDetailsState) => ({ payload }: Object) => {
  return {
    ...state,
    viewState: payload,
  };
};

export const onResetViewState = (state: AlertDetailsState) => () => ({
  ...state,
  viewState: initialViewState,
});

export const onExportAlertDetailsReport = (state: AlertDetailsState) => () => {
  return {
    ...state,
    reportGenerateInProgress: ++state.reportGenerateInProgress,
  };
};

export const onExportAlertDetailsReportSuccess = (state: AlertDetailsState) => () => {
  return {
    ...state,
    reportGenerateInProgress:
      state.reportGenerateInProgress > 0 ? --state.reportGenerateInProgress : 0,
  };
};

export default (state: AlertDetailsState = initialState, action: Object): AlertDetailsState => {
  const act = {
    [ExportAlertDetailsReport]: onExportAlertDetailsReport,
    [ExportAlertDetailsReportSuccess]: onExportAlertDetailsReportSuccess,
    [GetReportingAlertDetailsDefaultReportsSuccess]: onGetReportingAlertDetailsDefaultReportsSuccess,
    [GetReportingAlertDetailsReport]: onGetReportingAlertDetailsReport,
    [GetReportingAlertDetailsDefaultReportSuccess]: onGetReportingAlertDetailsDefaultReportSuccess,
    [GetReportingAlertDetailsReportSuccess]: onGetReportingAlertDetailsReportSuccess,
    [GetReportingAlertDetailsMyReportsSuccess]: onGetReportingAlertDetailsMySavedReportsSuccess,
    [GetReportingAlertDetailsChartsSuccess]: onGetReportingAlertDetailsChartsSuccess,
    [GetAlertDetailsFiltersSchemaSuccess]: onGetAlertDetailsFiltersSchemaSuccess,
    [SetAlertDetailsReportToSave]: onSetAlertDetailsReportToSave,
    [SetAlertDetailsReportViewState]: onSetViewState,
    [GetReportingAlertDetailsDefaultReports]: setLoading,
    [GetReportingAlertDetailsMyReports]: setLoading,
    [GetReportingAlertDetailsCharts]: setLoading,
    [ExportAlertDetailsReportError]: onError,
    [GetAlertDetailsFiltersSchemaError]: onError,
    [GetReportingAlertDetailsChartsError]: onError,
    [GetReportingAlertDetailsMyReportsError]: onError,
    [GetReportingAlertDetailsDefaultReportsError]: onError,
    [GetReportingAlertDetailsReportError]: onError,
    [LOCATION_CHANGE]: onResetViewState,
  };
  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
